/**
 * API client with authentication support.
 *
 * Automatically attaches the access token from the auth store,
 * handles 401 responses by attempting a silent token refresh,
 * and retries the original request once.
 *
 * Cross-References:
 *   - FR-AUTH-003: Token lifecycle
 *   - api-specifications/foundation.md §1.5: Refresh flow
 */
import { useAuthStore } from '@/stores/authStore';

const API_BASE = '/api/v1';

/**
 * Generic fetch wrapper for the VortexIntra API.
 *
 * - Injects `Authorization: Bearer <token>` when available.
 * - On 401, attempts a silent refresh and retries **once**.
 * - Parses JSON responses; throws `ApiError` on non-2xx.
 */
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const response = await _fetchWithAuth(path, options);

  // On 401, try one silent refresh cycle
  if (response.status === 401) {
    const refreshed = await useAuthStore.getState().refreshToken();
    if (refreshed) {
      const retried = await _fetchWithAuth(path, options);
      if (!retried.ok) {
        const body = await retried.json().catch(() => null);
        throw new ApiError(retried.status, body, _correlationFrom(retried, body));
      }
      return retried.json() as Promise<T>;
    }

    // Refresh failed → redirect to login (only if not already there)
    useAuthStore.getState().clearAuth();
    if (!window.location.pathname.startsWith('/login') &&
        !window.location.pathname.startsWith('/internal/login') &&
        !window.location.pathname.startsWith('/auth/')) {
      window.location.href = '/login';
    }
    throw new ApiError(401, null, _correlationFrom(response, null));
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(response.status, body, _correlationFrom(response, body));
  }

  // 204 No Content
  if (response.status === 204) return undefined as unknown as T;

  return response.json() as Promise<T>;
}

/**
 * Auth-aware blob fetch + download. Used for PDF / binary downloads
 * (PR / RFQ / PO / CapEx form PDFs) — a raw `<a href>` open in a new
 * tab loses the JWT bearer header and returns 403, so we fetch the
 * blob with auth, create an object URL, and trigger a download via
 * a synthetic anchor click. Suggested filename comes from the
 * Content-Disposition header when present.
 */
export async function downloadAuthedFile(
  path: string,
  suggestedFilename?: string,
): Promise<void> {
  const response = await _fetchWithAuth(path, { method: 'GET' });

  if (response.status === 401) {
    const refreshed = await useAuthStore.getState().refreshToken();
    if (!refreshed) {
      useAuthStore.getState().clearAuth();
      throw new ApiError(401, null, _correlationFrom(response, null));
    }
    const retried = await _fetchWithAuth(path, { method: 'GET' });
    if (!retried.ok) {
      const body = await retried.json().catch(() => null);
      throw new ApiError(retried.status, body, _correlationFrom(retried, body));
    }
    return _saveBlobFromResponse(retried, suggestedFilename);
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(response.status, body, _correlationFrom(response, body));
  }
  return _saveBlobFromResponse(response, suggestedFilename);
}

async function _saveBlobFromResponse(
  response: Response,
  suggestedFilename?: string,
): Promise<void> {
  // Pull filename from Content-Disposition if the server set one;
  // otherwise fall back to the caller's hint.
  let filename = suggestedFilename ?? 'download';
  const cd = response.headers.get('content-disposition');
  if (cd) {
    const match = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    if (match) filename = decodeURIComponent(match[1] ?? match[2] ?? filename);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Small delay before revoking so the browser has a chance to start
  // the download (revoking too eagerly can abort it in some browsers).
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/**
 * VM-M5-FU-22-e — auth-aware blob URL fetch, no download trigger.
 * Mirrors downloadAuthedFile() but returns a blob URL so the caller
 * can render the binary inline (e.g., <iframe src=...> for PDF
 * preview). Caller is responsible for `URL.revokeObjectURL(url)`
 * when done.
 */
export async function fetchAuthedBlobUrl(path: string): Promise<string> {
  let response = await _fetchWithAuth(path, { method: 'GET' });
  if (response.status === 401) {
    const refreshed = await useAuthStore.getState().refreshToken();
    if (!refreshed) {
      useAuthStore.getState().clearAuth();
      throw new ApiError(401, null, _correlationFrom(response, null));
    }
    response = await _fetchWithAuth(path, { method: 'GET' });
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(
      response.status, body, _correlationFrom(response, body),
    );
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/* ── internal helper ───────────────────────────────────────── */

async function _fetchWithAuth(
  path: string,
  options: RequestInit,
): Promise<Response> {
  const { accessToken } = useAuthStore.getState();
  const headers = new Headers(options.headers);

  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (accessToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  return fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include', // send httpOnly refresh cookie
  });
}

/* ── correlation ID extraction ─────────────────────────────── */

/**
 * Pull the request correlation ID from either the response header
 * (set by the backend's correlation-id middleware on every response)
 * or the RFC 9457 error body (the foundation includes it on
 * problem-detail responses). Header wins when both are present.
 *
 * Sprint INT-1 TKT-5: surface this in user-facing error renders so
 * operators can trace failures via /admin/audit without grepping.
 */
function _correlationFrom(
  response: Response,
  body: unknown,
): string | undefined {
  const header = response.headers.get('X-Correlation-ID');
  if (header) return header;
  if (body && typeof body === 'object' && 'correlation_id' in body) {
    const value = (body as { correlation_id?: unknown }).correlation_id;
    if (typeof value === 'string') return value;
  }
  return undefined;
}

/* ── error class ───────────────────────────────────────────── */

export class ApiError extends Error {
  constructor(
    public status: number,
    /** Parsed response, including FastAPI validation arrays; retained for field adapters. */
    public body: unknown,
    public correlationId?: string,
  ) {
    // Prefer the RFC 9457 problem-detail `detail` field when the server
    // gives us one — that's the human-readable explanation (e.g. "SoD
    // violation: the requester for this vendor cannot approve their own
    // vendor intake."). Fall back to the bare "API Error: 403" only when
    // the body is missing or unparsed. Consumers that catch ApiError and
    // surface `.message` get the meaningful text for free instead of a
    // status code that means nothing to end users.
    const detail =
      body && typeof body === 'object' && 'detail' in body
        ? (body as { detail?: unknown }).detail
        : undefined;
    super(typeof detail === 'string' && detail.trim()
      ? detail
      : `API Error: ${status}`);
    this.name = 'ApiError';
  }
}
